import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SettingsIcon from '@mui/icons-material/Settings';
import GridViewIcon from '@mui/icons-material/GridView';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';

import type { PlatformIntegration } from '../../../../types/platformIntegration';
import OrderDetailModal from '../modals/OrderDetailModal';
import CancelPhoneOrderModal, { CancelPhoneOrderPayload } from '../modals/CancelPhoneOrderModal';
import EditPhoneOrderItemsModal from '../modals/EditPhoneOrderItemsModal';
import AddPackageCustomerModal from '../modals/AddPackageCustomerModal';
import AddShippingAddressModal from '../modals/AddShippingAddressModal';
import CustomerDetailModal from '../modals/CustomerDetailModal';
import AllCallsModal from '../modals/AllCallsModal';
import SettingsModal from '../modals/SettingsModal';
import IncomingCallPopupModal from '../modals/IncomingCallPopupModal';
import IncomingCallNotification from './IncomingCallNotification';
import IncomingNewOrderNotification from './IncomingNewOrderNotification';
import PaymentModal, { PosOption, OtherPaymentOption } from '../modals/PaymentModal';
import ManualPhoneModal from '../modals/ManualPhoneModal';
import SelectCourierModal from '../modals/SelectCourierModal';
import CashierLoginModal, { CashierInfo } from '../../quickSale/components/modals/CashierLoginModal';
import NewOrderModal from '../modals/NewOrderModal';
import GetirOrderDetailModal from '../modals/GetirOrderDetailModal';
import QrOrderDetailModal from '../modals/QrOrderDetailModal';
import MigrosOrderDetailModal from '../modals/MigrosOrderDetailModal';
import YemeksepetiOrderDetailModal from '../modals/YemeksepetiOrderDetailModal';
import TrendyolOrderDetailModal from '../modals/TrendyolOrderDetailModal';
import TrendyolCancelModal from '../modals/TrendyolCancelModal';
import QuickSaleScreen from '../../quickSale/QuickSaleScreen';

import { PackageOrder, PackageCustomer, PhoneCall, MasaSiparisi, TrendyolOrder, StoreToggleInfo } from '../types';
import { QueuedCall } from '../hooks/usePhoneCalls';
import { QueuedNewOrder } from '../hooks/useNewOrderQueue';
import { getTrendyolPackageOrderId } from '../utils/helpers';
import { GetirCancelInfo } from '../hooks/useSignalR';
import { RingSoundOption } from '../hooks/useLocalSettings';
import type { NewOrderSoundStatus } from '../hooks/useNewOrderSound';
import { ShippingAddressDto } from '../../../../services/phoneCallService';
import { migrosApi } from '../../../../config/apiClient';
import { useTranslation } from 'react-i18next';

// ============================================================================
// SCREEN DATA TYPE (mirrors PackageOrderScreen's screenData shape)
// ============================================================================

export interface ScreenDataForModals {
  id: number;
  name: string; 
  branchId: string | null;
  packageScreenId: number | null;
  localServiceUrl: string | null;
  printingSettings: {
    printingMode: 'single' | 'category';
    defaultReceiptPrinter: { id: string; name: string } | null;
    definedPrinters: Array<{
      id: string;
      name: string;
    }>;
    receiptDisplaySettings?: {
      companyName?: string;
      companyAddress?: string;
      taxNumber?: string;
      headerType?: 'text' | 'logo';
      logoUrl?: string;
      logoMaxWidthPx?: number;
      logoMaxHeightPx?: number;
      standardFooterNote?: string;
      thankYouMessage?: string;
    };
    categoryPrinterAssignments?: { [categoryId: string]: string | null };
    adisyonPrinter?: { id: string; name: string } | null;
  } | null;
  useCashierSystem: boolean;
  paymentSettings: {
    cashPaymentCashboxId: string | null;
    cashPaymentCashboxName: string | null;
    posOptions: Array<{
      id: string;
      label: string;
      bankAccountId: string;
      subAccountId: string | null;
      bankAccountName: string | null;
    }>;
    otherOptions: Array<{
      id: string;
      label: string;
      cashboxId: string;
      cashboxName: string | null;
    }>;
  } | null;
  defaultWarehouseId: string | null;
  integrationSettings: {
    showPhoneOrders: boolean;
    showYemekSepetiOrders: boolean;
    showTrendyolOrders: boolean;
    showGetirOrders: boolean;
    showMigrosOrders: boolean;
    showKioskOrders: boolean;
    showQrMenuOrders: boolean;
    showEcommerceOrders: boolean;
  };
  platformIntegrations: PlatformIntegration[];
}

// ============================================================================
// PROPS INTERFACE
// ============================================================================

export interface PackageOrderModalsProps {
  // --- Theme ---
  themeColors: { primary: string; [key: string]: string };

  // --- Screen data ---
  screenData: ScreenDataForModals | null;
  companyId: string | undefined;

  // --- Orders ---
  orders: PackageOrder[];
  selectedOrder: PackageOrder | null;
  selectedOrderId: number | null;

  // --- Current customer ---
  currentCustomer: PackageCustomer | null;
  setCurrentCustomer: React.Dispatch<React.SetStateAction<PackageCustomer | null>>;

  // --- Incoming call ---
  incomingCall: PhoneCall | null;
  isCallAnswered: boolean;
  setIsCallAnswered: (v: boolean) => void;
  // Q6: F1.3 cross-call name leak guard'ının yeni API'si.
  // Aktif veya pending bir aramanın müşteri adını güvenli şekilde update eder.
  updateActiveCallCustomerName: (phone: string, name: string) => void;

  // --- Customer filters ---
  customerPhoneFilter: string | null;
  setCustomerPhoneFilter: React.Dispatch<React.SetStateAction<string | null>>;
  customerNameFilter: string | null;
  setCustomerNameFilter: React.Dispatch<React.SetStateAction<string | null>>;

  // --- Address ---
  selectedAddressId: number | null;
  setSelectedAddressId: React.Dispatch<React.SetStateAction<number | null>>;

  // --- Snackbar ---
  snackbar: { open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' };
  setSnackbar: React.Dispatch<React.SetStateAction<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>>;

  // --- Order Detail Modal ---
  detailModalOpen: boolean;
  handleCloseDetail: () => void;
  handleKitchenPrint: (
    orderId: number,
    source?: string,
    options?: {
      itemIdsToPrint?: number[];
      isAdditionalOrder?: boolean;
      preFetchedTrendyolOrder?: any;
    },
  ) => void;
  handleReceiptPrint: (
    orderId: number,
    source?: string,
    options?: { preFetchedTrendyolOrder?: any },
  ) => void;

  // --- Add Customer Modal ---
  addCustomerModalOpen: boolean;
  setAddCustomerModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  newCustomerPhone: string;

  // --- Add Address Modal ---
  addAddressModalOpen: boolean;
  setAddAddressModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleAddressAdded: (newAddress: ShippingAddressDto) => void;

  // --- Customer Detail Modal ---
  customerDetailModalOpen: boolean;
  setCustomerDetailModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedCallForDetail: PhoneCall | null;
  setSelectedCallForDetail: React.Dispatch<React.SetStateAction<PhoneCall | null>>;

  // --- All Calls Modal ---
  allCallsModalOpen: boolean;
  setAllCallsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleSelectRecentCall: (call: PhoneCall) => void;

  // --- Settings Modal ---
  settingsModalOpen: boolean;
  setSettingsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  phoneRingSoundEnabled: boolean;
  setPhoneRingSoundEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRingSound: string;
  setSelectedRingSound: React.Dispatch<React.SetStateAction<string>>;
  ringSoundOptions: RingSoundOption[];
  playRingSound: () => void;
  stopRingSound: () => void;
  testNewOrderSound?: () => Promise<NewOrderSoundStatus>;
  incomingCallModalEnabled: boolean;
  setIncomingCallModalEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  settingsScreenId?: number | null;
  settingsLocalServiceUrl?: string | null;
  onPrintingSettingsSaved?: () => void;

  // --- Incoming Call Popup Modal ---
  incomingCallPopupOpen: boolean;
  // Q3: opsiyonel dbCallId — verilirse o pending arama hedeflenir, yoksa activeCall
  answerCall: (dbCallId?: number) => void;
  endCallUnanswered: (dbCallId?: number, phone?: string) => Promise<void>;
  // F3 hibrit: herhangi bir modal açıksa popup yerine sağ üst köşede
  // IncomingCallNotification göster (data-loss + UX koruması).
  anyModalOpen: boolean;
  // Q5: Bekleyen aramalar kuyruğu (active varken gelen ek aramalar)
  pendingCalls: QueuedCall[];
  // Bulgu#1 düzeltme: aktif çağrının startedAt'ına ihtiyaç var (kronolojik FIFO render)
  activeCall: QueuedCall | null;
  // Birleşik kuyruk: held arama bildiriminden "Müşteriye Geç" → ekran karar
  // modalını (CallDecisionModal, next-in-queue) açar.
  onRequestCallDecision?: (qc: QueuedCall) => void;
  // Birleşik kuyruk: paket siparişi tamamlandığında (QuickSale onay) kuyrukta
  // bekleyen telefon varsa ekran karar modalını açar.
  onPackageOrderFlowCompleted?: () => void;

  // --- Quick Sale Modal ---
  quickSaleModalOpen: boolean;
  setQuickSaleModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  quickSaleOpenSettings: (() => void) | null;
  setQuickSaleOpenSettings: React.Dispatch<React.SetStateAction<(() => void) | null>>;
  quickSaleOpenProductSort: (() => void) | null;
  setQuickSaleOpenProductSort: React.Dispatch<React.SetStateAction<(() => void) | null>>;
  addOnOrderId: number | null;
  setAddOnOrderId: React.Dispatch<React.SetStateAction<number | null>>;
  currentCashier: CashierInfo | null;
  loadPackageOrders: () => void;

  // --- Payment Modal ---
  isPaymentModalOpen: boolean;
  setIsPaymentModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isProcessingPayment: boolean;
  handleCashPayment: () => void;
  handlePosPayment: (posOption: any) => void;
  handleOtherPayment: (option: OtherPaymentOption) => void;
  handleOnlinePaymentFromModal?: () => void;
  handlePavoP2pPayment?: () => void;
  hasActivePavoPayment?: boolean;
  onOpenPavoTracking?: () => void;

  /**
   * Online ödenmiş siparişler için (Yemeksepeti / Trendyol / Migros / Getir /
   * Web sitesi) Pavo cihazından mali fiş kestirme handler'ı. Açık olan detay
   * modalındaki "Online Fiş Yazdır" butonu basıldığında çağrılır.
   * Parent (PackageOrderScreen), `(orderId, source)` çiftini kullanarak
   * `packageOrders` listesinden ilgili `PackageOrder`'ı bulur ve REST-P2P
   * cihaz seçim modalını açar.
   */
  onRequestOnlineReceipt?: (orderId: number | string, source: string) => void;

  // --- PR1: Telefon Siparişi İptal Akışı ---
  /** OrderDetailModal'daki "İptal Et" butonu aktif edilsin mi (canCancelPhoneOrder sonucu). */
  cancelPhoneOrderAllowed?: boolean;
  /** İptal disable iken gösterilecek i18n tooltip key'i (REASON_I18N_KEY map'inden). */
  cancelPhoneOrderDisabledReasonI18nKey?: string;
  /** OrderDetailModal'daki "İptal Et" tıklandığında çağrılır — parent modal açar. */
  onRequestCancelPhoneOrder?: () => void;
  /** CancelPhoneOrderModal açık mı? */
  cancelPhoneOrderModalOpen?: boolean;
  /** CancelPhoneOrderModal kapatma callback'i. */
  onCancelPhoneOrderClose?: () => void;
  /** CancelPhoneOrderModal başlık/açıklamada gösterilecek sipariş no. */
  cancelPhoneOrderTargetOrderNo?: string | number;
  /** Modal "İptal Et" tıklanınca parent çağrılır; reject olursa modal açık kalır. */
  onCancelPhoneOrderConfirmed?: (payload: CancelPhoneOrderPayload) => Promise<void>;

  // --- PR2: Telefon Siparişi Sepet Düzenleme Akışı ---
  /** OrderDetailModal'daki "Sepeti Düzenle" butonu aktif mi (canEditPhoneOrderItems sonucu). */
  editCartAllowed?: boolean;
  /** Disabled iken tooltip i18n key'i (REASON_I18N_KEY map'inden). */
  editCartDisabledReasonI18nKey?: string;
  /** "Sepeti Düzenle" butonuna tıklanınca çağrılır — parent modal'ı açar. */
  onRequestEditCart?: () => void;
  /** EditPhoneOrderItemsModal açık mı? */
  editCartModalOpen?: boolean;
  onEditCartClose?: () => void;
  /** Modal başlığında gösterilecek sipariş no. */
  editCartTargetOrderId?: number | null;
  editCartTargetOrderShortNo?: string | null;
  editCartTargetCustomerName?: string | null;
  /** Her başarılı sepet mutation sonrası çağrılır — parent listeyi tazeleyebilir. */
  onEditCartMutated?: () => void;
  /** Modal'daki "Yeni Ürün Ekle" butonu — parent QuickSale ilave-sipariş modunu açar. */
  onEditCartAddNewItem?: (orderId: number) => void;
  /**
   * PR2: OrderDetailModal'ı zorla yeniden fetch ettirmek için monoton sayaç.
   * EditCart modal'da her başarılı mutation sonrası incremented; OrderDetailModal
   * kendi `useEffect` dep'inde bu prop'u izler ve sipariş detayını tazeler.
   */
  orderDetailRefreshKey?: number;

  // --- Manual Phone Modal ---
  isManualPhoneModalOpen: boolean;
  setIsManualPhoneModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  manualPhoneNumber: string;
  setManualPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
  isSearchingPhone: boolean;
  handleManualPhoneSearch: () => void;

  // --- Select Courier Modal ---
  selectCourierModalOpen: boolean;
  setSelectCourierModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleCourierSelect: (courierId: number, courierFirebaseUid: string, courierName: string) => void;

  // --- Cashier Login Modal ---
  isCashierLoginModalOpen: boolean;
  setIsCashierLoginModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleCashierLogin: (cashierInfo: CashierInfo) => void;

  // --- New Order Modal + Queue (O6) ---
  // Eski 3 prop (newOrderModalOpen, newOrderData, newTrendyolOrderData) yerine
  // useNewOrderQueue hook'tan gelen dual state geçilir. Aktif sipariş
  // NewOrderModal'da gösterilir, bekleyenler sağ üstte IncomingNewOrderNotification.
  activeNewOrder: QueuedNewOrder | null;
  pendingNewOrders: QueuedNewOrder[];
  completeActiveNewOrder: () => void;
  handleCloseNewOrderModal: () => void;
  autoAcceptSeconds?: number;
  onAutoAccept?: () => void;
  // Bildirim içi "Onayla ve Yazdır": bekleyen sipariş modala düşmeden
  // pending kuyruğundan onaylanıp yazdırılır (kasiyer telefonla meşgulken).
  onAcceptAndPrintPending?: (q: QueuedNewOrder) => void;
  /** Manuel "Onayla ve Yazdır" butonu — oto kabul ayarından bağımsız her zaman aktif */
  onAcceptAndPrint?: () => void;

  // --- Getir Detail Modal ---
  getirDetailModalOpen: boolean;
  setGetirDetailModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  getirDetailOrderId: number | null;
  setGetirDetailOrderId: React.Dispatch<React.SetStateAction<number | null>>;

  // --- Getir Cancel Modal ---
  getirCancelModalOpen: boolean;
  handleCloseGetirCancelModal: () => void;
  cancelledOrderInfo: GetirCancelInfo | null;

  // --- QR Order Detail Modal ---
  qrOrderDetailModalOpen: boolean;
  setQrOrderDetailModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedQrOrder: MasaSiparisi | null;
  updateMasaSiparisiStatus: (orderId: number, status: string) => Promise<any>;
  fetchMasaSiparisleri: () => void;

  // --- Getir Restaurant Closed Modal ---
  getirRestaurantClosedModalOpen: boolean;
  setGetirRestaurantClosedModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  getirRestaurantOpening: Record<number, boolean>;
  setGetirRestaurantOpening: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  openGetirRestaurant: (integrationId?: number) => Promise<{ success: boolean; message?: string }>;

  // --- Migros Cancel Modal ---
  migrosCancelModalOpen: boolean;
  handleCloseMigrosCancelModal: () => void;
  migrosCancelledOrderInfo: any;

  // --- Migros Restaurant Closed Modal ---
  migrosRestaurantClosedModalOpen: boolean;
  setMigrosRestaurantClosedModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  migrosRestaurantOpening: Record<number, boolean>;
  setMigrosRestaurantOpening: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  openMigrosRestaurant: (integrationId?: number) => Promise<boolean>;

  // --- Yemeksepeti Vendor Closed Modal ---
  ysVendorClosedModalOpen: boolean;
  setYsVendorClosedModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  ysVendorOpening: Record<number, boolean>;
  setYsVendorOpening: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  openYemeksepetiVendor: (integrationId?: number) => Promise<boolean>;

  // --- Yemeksepeti Reject Modal ---
  ysRejectModalOpen: boolean;
  setYsRejectModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  ysRejectOrderId: number | null;
  setYsRejectOrderId: React.Dispatch<React.SetStateAction<number | null>>;
  ysRejectReasons: any[];
  selectedYsRejectReason: string | null;
  setSelectedYsRejectReason: React.Dispatch<React.SetStateAction<string | null>>;
  updateYemeksepetiOrderStatus: (orderId: number, action: string, reason?: string) => Promise<any>;
  fetchYemeksepetiOrders: () => Promise<void>;

  // --- Yemeksepeti Cancel Modal ---
  ysCancelModalOpen: boolean;
  setYsCancelModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  ysCancelledOrderInfo: any;
  setYsCancelledOrderInfo: React.Dispatch<React.SetStateAction<any>>;

  // --- Migros Reject Modal ---
  migrosRejectModalOpen: boolean;
  setMigrosRejectModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  migrosRejectOrderId: number | null;
  setMigrosRejectOrderId: React.Dispatch<React.SetStateAction<number | null>>;
  migrosRejectOrderStatus: string;
  setMigrosRejectOrderStatus: React.Dispatch<React.SetStateAction<string>>;
  migrosCancelReasons: any[];
  selectedMigrosCancelReasonId: number | null;
  setSelectedMigrosCancelReasonId: React.Dispatch<React.SetStateAction<number | null>>;
  fetchMigrosOrders: () => Promise<void>;

  // --- Migros Detail Modal ---
  migrosDetailModalOpen: boolean;
  setMigrosDetailModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  migrosDetailOrderId: number | null;
  setMigrosDetailOrderId: React.Dispatch<React.SetStateAction<number | null>>;

  // --- Yemeksepeti Detail Modal ---
  ysDetailModalOpen: boolean;
  setYsDetailModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  ysDetailOrderId: number | null;
  setYsDetailOrderId: React.Dispatch<React.SetStateAction<number | null>>;

  // --- Trendyol Detail Modal ---
  trendyolDetailModalOpen: boolean;
  setTrendyolDetailModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  trendyolDetailOrder: TrendyolOrder | null;
  setTrendyolDetailOrder: React.Dispatch<React.SetStateAction<TrendyolOrder | null>>;
  trendyolProductDescriptions?: Map<number, string>;

  // --- Trendyol Cancel Modal (kullanıcı iptal) ---
  trendyolCancelModalOpen: boolean;
  setTrendyolCancelModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  trendyolCancelOrder: TrendyolOrder | null;
  setTrendyolCancelOrder: React.Dispatch<React.SetStateAction<TrendyolOrder | null>>;
  cancelTrendyolOrder: (packageId: string, itemIdList: string[], reasonId: number) => Promise<boolean>;

  // --- Trendyol Cancel Notification Modal (platform iptal bildirimi) ---
  trendyolCancelNotifModalOpen: boolean;
  handleCloseTrendyolCancelNotifModal: () => void;
  trendyolCancelledNotifOrder: TrendyolOrder | null;

  // --- Trendyol Vendor Closed Modal ---
  trendyolVendorClosedModalOpen: boolean;
  setTrendyolVendorClosedModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  trendyolVendorOpening: Record<number, boolean>;
  setTrendyolVendorOpening: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  openTrendyolVendor: (integrationId?: number) => Promise<boolean>;

  // --- Close Restaurant Modal ---
  closeRestaurantModalOpen: boolean;
  setCloseRestaurantModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  closeRestaurantPlatform: string | null;
  closingRestaurant: boolean;
  setClosingRestaurant: React.Dispatch<React.SetStateAction<boolean>>;
  handleCloseRestaurantConfirm: (reason?: string, integrationId?: number) => Promise<void>;

  /** Platform sekmesi başına mağaza listesi; aç/kapat düğmeleri buradan üretilir. */
  platformStores?: Record<string, StoreToggleInfo[]>;
}

/** Entegrasyon kimliği olmayan (tek mağazalı) kurulumların yükleniyor anahtarı. */
const SINGLE_STORE_KEY = 0;

/** Herhangi bir mağaza için açma işlemi sürüyor mu? İptal düğmesini kilitler. */
const isAnyOpening = (map: Record<number, boolean>) => Object.values(map || {}).some(Boolean);

interface StoreOpenButtonsProps {
  stores: StoreToggleInfo[];
  openingMap: Record<number, boolean>;
  setOpeningMap: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  onOpen: (integrationId?: number) => Promise<boolean | { success: boolean; message?: string }>;
  confirmLabel: string;
  openingLabel: string;
  onSuccess: (storeLabel?: string) => void;
  onFailure: (message?: string, storeLabel?: string) => void;
  /** Son kapalı mağaza da açıldığında modalı kapatır. */
  onAllOpened: () => void;
}

/**
 * Kapalı mağazalar için "Restoranı Aç" düğmelerini üretir.
 *
 * Tek mağazalı kurulumda marka adı yazılmadan tek düğme çıkar — görünüm
 * bugünküyle aynı kalır. Çoklu mağazada her kapalı marka için ayrı düğme
 * ve ayrı yükleniyor durumu olur; birine basmak diğerini kilitlemez.
 */
const StoreOpenButtons: React.FC<StoreOpenButtonsProps> = ({
  stores,
  openingMap,
  setOpeningMap,
  onOpen,
  confirmLabel,
  openingLabel,
  onSuccess,
  onFailure,
  onAllOpened,
}) => {
  const closedStores = stores.filter(s => !s.isOpen);
  const targets: Array<{ key: number; integrationId?: number; label?: string }> =
    stores.length > 1 && closedStores.length > 0
      ? closedStores.map(s => ({ key: s.integrationId, integrationId: s.integrationId, label: s.label }))
      : [{ key: stores[0]?.integrationId ?? SINGLE_STORE_KEY, integrationId: stores[0]?.integrationId }];

  const handleClick = async (target: { key: number; integrationId?: number; label?: string }) => {
    setOpeningMap(prev => ({ ...prev, [target.key]: true }));
    try {
      const result = await onOpen(target.integrationId);
      const success = typeof result === 'boolean' ? result : result.success;
      const message = typeof result === 'boolean' ? undefined : result.message;
      if (success) {
        onSuccess(target.label);
        if (targets.length <= 1) onAllOpened();
      } else {
        onFailure(message, target.label);
      }
    } catch (err: any) {
      onFailure(err?.message, target.label);
    } finally {
      setOpeningMap(prev => ({ ...prev, [target.key]: false }));
    }
  };

  return (
    <>
      {targets.map(target => {
        const isOpening = !!openingMap[target.key];
        return (
          <Button
            key={target.key}
            variant="contained"
            color="success"
            disabled={isOpening}
            startIcon={isOpening ? <CircularProgress size={18} color="inherit" /> : null}
            onClick={() => handleClick(target)}
          >
            {isOpening ? openingLabel : target.label ? `${confirmLabel}: ${target.label}` : confirmLabel}
          </Button>
        );
      })}
    </>
  );
};

// ============================================================================
// COMPONENT
// ============================================================================

const PackageOrderModals: React.FC<PackageOrderModalsProps> = (props) => {
  const { t } = useTranslation('salesSystem');
  const {
    themeColors,
    screenData,
    companyId,
    orders,
    selectedOrder,
    selectedOrderId,
    currentCustomer,
    setCurrentCustomer,
    incomingCall,
    setIsCallAnswered,
    updateActiveCallCustomerName,
    customerPhoneFilter,
    setCustomerPhoneFilter,
    setCustomerNameFilter,
    selectedAddressId,
    setSelectedAddressId,
    snackbar,
    setSnackbar,
    detailModalOpen,
    handleCloseDetail,
    handleKitchenPrint,
    handleReceiptPrint,
    addCustomerModalOpen,
    setAddCustomerModalOpen,
    newCustomerPhone,
    addAddressModalOpen,
    setAddAddressModalOpen,
    handleAddressAdded,
    customerDetailModalOpen,
    setCustomerDetailModalOpen,
    selectedCallForDetail,
    setSelectedCallForDetail,
    allCallsModalOpen,
    setAllCallsModalOpen,
    handleSelectRecentCall,
    settingsModalOpen,
    setSettingsModalOpen,
    phoneRingSoundEnabled,
    setPhoneRingSoundEnabled,
    selectedRingSound,
    setSelectedRingSound,
    ringSoundOptions,
    playRingSound,
    stopRingSound,
    testNewOrderSound,
    incomingCallModalEnabled,
    setIncomingCallModalEnabled,
    settingsScreenId,
    settingsLocalServiceUrl,
    onPrintingSettingsSaved,
    incomingCallPopupOpen,
    answerCall,
    endCallUnanswered,
    anyModalOpen,
    pendingCalls,
    activeCall,
    onRequestCallDecision,
    onPackageOrderFlowCompleted,
    quickSaleModalOpen,
    setQuickSaleModalOpen,
    quickSaleOpenSettings,
    setQuickSaleOpenSettings,
    quickSaleOpenProductSort,
    setQuickSaleOpenProductSort,
    addOnOrderId,
    setAddOnOrderId,
    currentCashier,
    loadPackageOrders,
    isPaymentModalOpen,
    setIsPaymentModalOpen,
    isProcessingPayment,
    handleCashPayment,
    handlePosPayment,
    handleOtherPayment,
    handleOnlinePaymentFromModal,
    handlePavoP2pPayment,
    hasActivePavoPayment,
    onOpenPavoTracking,
    onRequestOnlineReceipt,
    cancelPhoneOrderAllowed,
    cancelPhoneOrderDisabledReasonI18nKey,
    onRequestCancelPhoneOrder,
    cancelPhoneOrderModalOpen,
    onCancelPhoneOrderClose,
    cancelPhoneOrderTargetOrderNo,
    onCancelPhoneOrderConfirmed,
    editCartAllowed,
    editCartDisabledReasonI18nKey,
    onRequestEditCart,
    editCartModalOpen,
    onEditCartClose,
    editCartTargetOrderId,
    editCartTargetOrderShortNo,
    editCartTargetCustomerName,
    onEditCartMutated,
    onEditCartAddNewItem,
    orderDetailRefreshKey,
    isManualPhoneModalOpen,
    setIsManualPhoneModalOpen,
    manualPhoneNumber,
    setManualPhoneNumber,
    isSearchingPhone,
    handleManualPhoneSearch,
    selectCourierModalOpen,
    setSelectCourierModalOpen,
    handleCourierSelect,
    isCashierLoginModalOpen,
    setIsCashierLoginModalOpen,
    handleCashierLogin,
    activeNewOrder,
    pendingNewOrders,
    handleCloseNewOrderModal,
    autoAcceptSeconds,
    onAutoAccept,
    onAcceptAndPrint,
    onAcceptAndPrintPending,
    getirDetailModalOpen,
    setGetirDetailModalOpen,
    getirDetailOrderId,
    setGetirDetailOrderId,
    getirCancelModalOpen,
    handleCloseGetirCancelModal,
    cancelledOrderInfo,
    qrOrderDetailModalOpen,
    setQrOrderDetailModalOpen,
    selectedQrOrder,
    updateMasaSiparisiStatus,
    fetchMasaSiparisleri,
    getirRestaurantClosedModalOpen,
    setGetirRestaurantClosedModalOpen,
    getirRestaurantOpening,
    setGetirRestaurantOpening,
    openGetirRestaurant,
    migrosCancelModalOpen,
    handleCloseMigrosCancelModal,
    migrosCancelledOrderInfo,
    migrosRestaurantClosedModalOpen,
    setMigrosRestaurantClosedModalOpen,
    migrosRestaurantOpening,
    setMigrosRestaurantOpening,
    openMigrosRestaurant,
    ysVendorClosedModalOpen,
    setYsVendorClosedModalOpen,
    ysVendorOpening,
    setYsVendorOpening,
    openYemeksepetiVendor,
    ysRejectModalOpen,
    setYsRejectModalOpen,
    ysRejectOrderId,
    setYsRejectOrderId,
    ysRejectReasons,
    selectedYsRejectReason,
    setSelectedYsRejectReason,
    updateYemeksepetiOrderStatus,
    fetchYemeksepetiOrders,
    ysCancelModalOpen,
    setYsCancelModalOpen,
    ysCancelledOrderInfo,
    setYsCancelledOrderInfo,
    migrosRejectModalOpen,
    setMigrosRejectModalOpen,
    migrosRejectOrderId,
    setMigrosRejectOrderId,
    migrosRejectOrderStatus,
    setMigrosRejectOrderStatus,
    migrosCancelReasons,
    selectedMigrosCancelReasonId,
    setSelectedMigrosCancelReasonId,
    fetchMigrosOrders,
    migrosDetailModalOpen,
    setMigrosDetailModalOpen,
    migrosDetailOrderId,
    setMigrosDetailOrderId,
    ysDetailModalOpen,
    setYsDetailModalOpen,
    ysDetailOrderId,
    setYsDetailOrderId,
    trendyolDetailModalOpen,
    setTrendyolDetailModalOpen,
    trendyolDetailOrder,
    setTrendyolDetailOrder,
    trendyolProductDescriptions,
    trendyolCancelModalOpen,
    setTrendyolCancelModalOpen,
    trendyolCancelOrder,
    setTrendyolCancelOrder,
    cancelTrendyolOrder,
    trendyolCancelNotifModalOpen,
    handleCloseTrendyolCancelNotifModal,
    trendyolCancelledNotifOrder,
    trendyolVendorClosedModalOpen,
    setTrendyolVendorClosedModalOpen,
    trendyolVendorOpening,
    setTrendyolVendorOpening,
    openTrendyolVendor,
    closeRestaurantModalOpen,
    setCloseRestaurantModalOpen,
    closeRestaurantPlatform,
    closingRestaurant,
    setClosingRestaurant,
    handleCloseRestaurantConfirm,
    platformStores,
  } = props;

  const [ysCloseReason, setYsCloseReason] = React.useState('OTHER');
  // Kapatılacak mağaza; tek mağazalı kurulumda seçim sorulmaz.
  const [closeStoreId, setCloseStoreId] = React.useState<number | ''>('');

  const closableStores = closeRestaurantPlatform
    ? (platformStores?.[closeRestaurantPlatform] || []).filter(s => s.isOpen)
    : [];

  React.useEffect(() => {
    if (!closeRestaurantModalOpen) return;
    setCloseStoreId(closableStores.length === 1 ? closableStores[0].integrationId : '');
    // Modal her açıldığında seçim sıfırlanır; kullanıcı yanlış markayı kapatmasın.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeRestaurantModalOpen, closeRestaurantPlatform]);

  return (
    <>
      {/* ================================================================== */}
      {/* ORDER DETAIL MODAL */}
      {/* ================================================================== */}
      <OrderDetailModal
        open={detailModalOpen}
        onClose={handleCloseDetail}
        orderId={selectedOrderId}
        onKitchenPrint={handleKitchenPrint}
        onReceiptPrint={handleReceiptPrint}
        screenSettings={screenData ? {
          localServiceUrl: screenData.localServiceUrl || undefined,
          printingSettings: screenData.printingSettings ? {
            defaultReceiptPrinter: screenData.printingSettings.defaultReceiptPrinter as { id: string; name: string } | null | undefined,
            adisyonPrinter: screenData.printingSettings.adisyonPrinter || null,
            receiptDisplaySettings: screenData.printingSettings.receiptDisplaySettings,
          } : undefined,
          platformIntegrations: screenData.platformIntegrations,
        } : null}
        onRequestOnlineReceipt={onRequestOnlineReceipt && selectedOrderId
          ? () => onRequestOnlineReceipt(selectedOrderId, 'phone')
          : undefined}
        onCancelOrder={onRequestCancelPhoneOrder}
        cancelAllowed={cancelPhoneOrderAllowed}
        cancelDisabledReasonI18nKey={cancelPhoneOrderDisabledReasonI18nKey}
        onEditCart={onRequestEditCart}
        editCartAllowed={editCartAllowed}
        editCartDisabledReasonI18nKey={editCartDisabledReasonI18nKey}
        refreshKey={orderDetailRefreshKey}
      />

      {/* ================================================================== */}
      {/* PR1: CANCEL PHONE ORDER MODAL */}
      {/* ================================================================== */}
      {onCancelPhoneOrderConfirmed && onCancelPhoneOrderClose && (
        <CancelPhoneOrderModal
          open={!!cancelPhoneOrderModalOpen}
          onClose={onCancelPhoneOrderClose}
          onConfirm={onCancelPhoneOrderConfirmed}
          orderNo={cancelPhoneOrderTargetOrderNo ?? ''}
        />
      )}

      {/* ================================================================== */}
      {/* PR2: EDIT PHONE ORDER ITEMS MODAL */}
      {/* ================================================================== */}
      {onEditCartClose && (
        <EditPhoneOrderItemsModal
          open={!!editCartModalOpen}
          orderId={editCartTargetOrderId ?? null}
          orderShortNo={editCartTargetOrderShortNo ?? null}
          customerName={editCartTargetCustomerName ?? null}
          onClose={onEditCartClose}
          onMutated={onEditCartMutated}
          onAddNewItem={onEditCartAddNewItem}
        />
      )}

      {/* ================================================================== */}
      {/* ADD PACKAGE CUSTOMER MODAL */}
      {/* ================================================================== */}
      <AddPackageCustomerModal
        open={addCustomerModalOpen}
        onClose={() => {
          setAddCustomerModalOpen(false);
        }}
        callerPhone={newCustomerPhone}
        branchId={screenData?.branchId || undefined}
        onCustomerCreated={(customer) => {
          console.log('✅ Yeni müşteri oluşturuldu:', customer);
          
          setCurrentCustomer({
            id: customer.accountId,
            name: customer.customerName,
            phone: newCustomerPhone,
            addresses: customer.address ? [{
              id: customer.address.Id,
              label: customer.address.AddressLabel || 'Ev',
              address: `${customer.address.District}, ${customer.address.City} - ${customer.address.Address}`,
              isDefault: customer.address.IsDefault || true,
            }] : [],
            totalOrders: 0,
            lastOrderDate: '',
          });
          
          // Q6: F1.3 cross-call name leak koruması — yeni kuyruk API'sine taşındı.
          // updateActiveCallCustomerName hem activeCall hem pendingCalls'ı tarar,
          // sadece phone match'leyen QueuedCall'un customerName'ini günceller.
          // Yani kayıt esnasında yeni bir arama (farklı numara) gelse bile
          // ona yanlış müşteri adı yazılmaz.
          updateActiveCallCustomerName(newCustomerPhone, customer.customerName);
          
          setIsCallAnswered(true);
          
          setCustomerPhoneFilter(newCustomerPhone);
          setCustomerNameFilter(customer.customerName);
          
          if (customer.address?.Id) {
            setSelectedAddressId(customer.address.Id);
          }
          
          setAddCustomerModalOpen(false);
          setSnackbar({ open: true, message: `✅ Müşteri kaydedildi: ${customer.customerName}`, severity: 'success' });
        }}
      />

      {/* ================================================================== */}
      {/* ADD SHIPPING ADDRESS MODAL */}
      {/* ================================================================== */}
      {currentCustomer && currentCustomer.id > 0 && (
        <AddShippingAddressModal
          open={addAddressModalOpen}
          onClose={() => setAddAddressModalOpen(false)}
          accountId={currentCustomer.id}
          customerName={currentCustomer.name}
          customerPhone={currentCustomer.phone}
          onAddressAdded={handleAddressAdded}
        />
      )}

      {/* ================================================================== */}
      {/* CUSTOMER DETAIL MODAL */}
      {/* ================================================================== */}
      <CustomerDetailModal
        open={customerDetailModalOpen}
        onClose={() => {
          setCustomerDetailModalOpen(false);
          setSelectedCallForDetail(null);
        }}
        phone={selectedCallForDetail?.phone || customerPhoneFilter || ''}
        customerName={selectedCallForDetail?.customerName}
        accountId={selectedCallForDetail?.accountId}
      />

      {/* ================================================================== */}
      {/* TÜM ARAMALAR MODAL */}
      {/* ================================================================== */}
      <AllCallsModal
        open={allCallsModalOpen}
        onClose={() => setAllCallsModalOpen(false)}
        packageScreenId={screenData?.packageScreenId || 0}
        onSelectCall={(call) => {
          handleSelectRecentCall(call);
        }}
      />

      {/* ================================================================== */}
      {/* AYARLAR MODAL */}
      {/* ================================================================== */}
      <SettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        themeColor={themeColors.primary}
        phoneRingSoundEnabled={phoneRingSoundEnabled}
        setPhoneRingSoundEnabled={setPhoneRingSoundEnabled}
        selectedRingSound={selectedRingSound}
        setSelectedRingSound={setSelectedRingSound}
        ringSoundOptions={ringSoundOptions}
        playRingSound={playRingSound}
        stopRingSound={stopRingSound}
        testNewOrderSound={testNewOrderSound}
        incomingCallModalEnabled={incomingCallModalEnabled}
        setIncomingCallModalEnabled={setIncomingCallModalEnabled}
        screenId={settingsScreenId}
        localServiceUrl={settingsLocalServiceUrl}
        onPrintingSettingsSaved={onPrintingSettingsSaved}
      />

      {/* ================================================================== */}
      {/* GELEN ARAMA — Hibrit (anyModalOpen) + Birleşik FIFO Kuyruk          */}
      {/*                                                                    */}
      {/* AKTİF ÇAĞRI iki render varyantı:                                   */}
      {/*  - anyModalOpen=false → IncomingCallPopupModal (FULL-SCREEN burada) */}
      {/*  - anyModalOpen=true  → aşağıdaki BİRLEŞİK STACK içinde            */}
      {/*    'phone-active' olarak startedAt'a göre kronolojik render edilir  */}
      {/*                                                                    */}
      {/* BEKLEYEN ARAMALAR + BEKLEYEN YEMEK SİPARİŞLERİ:                    */}
      {/*  - Aşağıdaki BİRLEŞİK STACK içinde HEPSİ kronolojik FIFO render    */}
      {/*    edilir. Yeni gelen her bildirim, mevcut en alttakinin altına    */}
      {/*    gelir — tür farketmez (kullanıcı kuralı, kronolojik FIFO).      */}
      {/* ================================================================== */}
      {!anyModalOpen && (
        <IncomingCallPopupModal
          open={incomingCallPopupOpen && incomingCallModalEnabled}
          incomingCall={incomingCall}
          themeColor={themeColors.primary}
          onAnswer={() => answerCall()}
          onReject={() => endCallUnanswered()}
        />
      )}

      {/* ================================================================== */}
      {/* BİRLEŞİK STACK — Kronolojik FIFO (aktif çağrı + pending'ler)       */}
      {/* ================================================================== */}
      {/* KULLANICI KURALI (kronolojik FIFO):                                */}
      {/*   "Yeni gelen her bildirim, mevcut en alttakinin ALTINA gelir."    */}
      {/*   - Önce yemek varsa, sonra gelen telefon yemek'in altına          */}
      {/*   - Önce telefon varsa, sonra gelen yemek telefonun altına         */}
      {/*   - Tür farketmez, sadece zaman damgası (startedAt / enqueuedAt)   */}
      {/*                                                                    */}
      {/* AKTİF ÇAĞRI:                                                       */}
      {/*  - anyModalOpen=false → IncomingCallPopupModal (yukarıda, full-    */}
      {/*    screen). Slot işgali yok, pending'ler altında popup tarafından  */}
      {/*    kaplanmış olabilir.                                             */}
      {/*  - anyModalOpen=true  → BU listeye 'phone-active' olarak eklenir   */}
      {/*    ve startedAt'ına göre kronolojik sıralanır.                     */}
      {/*                                                                    */}
      {/* MAX 3 GÖRÜNÜR BİLDİRİM — daha fazlası gizlenir.                    */}
      {(() => {
        type StackItem =
          | { kind: 'phone-active'; data: QueuedCall; ts: number }
          | { kind: 'phone-pending'; data: QueuedCall; ts: number; phoneIdx: number }
          | { kind: 'food'; data: QueuedNewOrder; ts: number; foodIdx: number };

        const items: StackItem[] = [];

        // Aktif çağrı — sadece anyModalOpen=true ise notification olarak render
        // (anyModalOpen=false ise zaten popup full-screen yukarıda render ediliyor)
        if (incomingCallModalEnabled && anyModalOpen && activeCall) {
          items.push({
            kind: 'phone-active',
            data: activeCall,
            ts: activeCall.startedAt,
          });
        }
        // Pending telefonlar (incomingCallModalEnabled=false ise hariç)
        if (incomingCallModalEnabled) {
          pendingCalls.forEach((pc, i) => {
            items.push({
              kind: 'phone-pending',
              data: pc,
              ts: pc.startedAt,
              phoneIdx: i,
            });
          });
        }
        // Pending yemekler — kullanıcı ayarından bağımsız her zaman göster
        pendingNewOrders.forEach((qno, i) => {
          items.push({
            kind: 'food',
            data: qno,
            ts: qno.enqueuedAt,
            foodIdx: i,
          });
        });

        if (items.length === 0) return null;

        // Kronolojik sırala — en eski (en küçük ts) en üstte (slot 0)
        items.sort((a, b) => a.ts - b.ts);

        // Max 3 görünür bildirim (sağ üstte 3'ten fazla yığılma görsel kaos)
        return items.slice(0, 3).map((item, idx) => {
          if (item.kind === 'phone-active') {
            return (
              <IncomingCallNotification
                key={`phone-active-${item.data.dbCallId ?? item.data.call.id}`}
                open={incomingCallPopupOpen && incomingCallModalEnabled}
                incomingCall={item.data.call}
                themeColor={themeColors.primary}
                onAnswer={() => answerCall()}
                onReject={() => endCallUnanswered()}
                stackIndex={idx}
              />
            );
          }
          if (item.kind === 'phone-pending') {
            const pc = item.data;
            return (
              <IncomingCallNotification
                key={`phone-pending-${pc.dbCallId ?? pc.call.id}`}
                open={true}
                incomingCall={pc.call}
                themeColor={themeColors.primary}
                // Held arama: yeşil buton karar modalını açar (Müşteriye Geç) —
                // doğrudan answerCall ile eski müşteri bağlamı ezilmesin.
                onAnswer={() => {
                  if (pc.isHeld && onRequestCallDecision) {
                    onRequestCallDecision(pc);
                  } else {
                    answerCall(pc.dbCallId ?? undefined);
                  }
                }}
                onReject={() => endCallUnanswered(pc.dbCallId ?? undefined, pc.call.phone)}
                stackIndex={idx}
                isPending={true}
                isHeld={pc.isHeld}
                ringEnded={pc.ringEnded}
                pendingNumber={item.phoneIdx + 1}
              />
            );
          }
          return (
            <IncomingNewOrderNotification
              key={`food-${item.data.key}`}
              open
              queuedOrder={item.data}
              stackIndex={idx}
              pendingNumber={item.foodIdx + 1}
              onAcceptAndPrint={
                onAcceptAndPrintPending
                  ? () => onAcceptAndPrintPending(item.data)
                  : undefined
              }
            />
          );
        });
      })()}

      {/* ================================================================== */}
      {/* HIZLI SATIŞ MODAL (PAKET SİPARİŞİ) */}
      {/* ================================================================== */}
      {/* KAPANMA KORUMASI: backdrop tıklaması ve ESC modalı KAPATMAZ.        */}
      {/* Dakikalarca girilen sepet, yanlışlıkla dışarı tıklamayla (veya sağ  */}
      {/* üstteki bildirim butonlarını kullanırken) kaybolmasın. Modal        */}
      {/* yalnızca başlıktaki X butonu ve sipariş tamamlama akışıyla kapanır. */}
      <Dialog
        open={quickSaleModalOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setQuickSaleModalOpen(false);
        }}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '75vw',
            height: '85vh',
            maxWidth: '1400px',
            maxHeight: '900px',
            borderRadius: 2,
            overflow: 'hidden',
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: themeColors.primary,
            color: 'white',
            px: 2,
            py: 1,
            minHeight: 48,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon />
            <Typography variant="subtitle1" fontWeight="bold">
              {addOnOrderId 
                ? `İlave Sipariş - #${orders.find(o => o.id === addOnOrderId)?.shortOrderNo || addOnOrderId}`
                : `Paket Siparişi - ${currentCustomer?.name || 'Müşteri'}`
              }
            </Typography>
            {addOnOrderId && (
              <Chip 
                size="small" 
                label="➕ Mevcut Siparişe Ekleniyor" 
                sx={{ bgcolor: 'success.main', color: 'white', fontWeight: 'bold' }} 
              />
            )}
            {currentCustomer?.phone && !addOnOrderId && (
              <Chip 
                size="small" 
                label={currentCustomer.phone} 
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {quickSaleOpenSettings && (
              <Tooltip title="Ekran Ayarları">
                <IconButton 
                  size="small" 
                  onClick={quickSaleOpenSettings}
                  sx={{ color: 'white' }}
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            )}
            {/* ✅ YENİ: Ürün Kartı Düzeni — normal hızlı satıştaki menü seçeneğinin
                paket modu karşılığı (PIN sonrası sürükle-bırak düzenleme modu). */}
            {quickSaleOpenProductSort && (
              <Tooltip title="Ürün Kartı Düzeni">
                <IconButton
                  size="small"
                  onClick={quickSaleOpenProductSort}
                  sx={{ color: 'white' }}
                >
                  <GridViewIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('orderManagement.categoryMapping.actions.close')}>
              <IconButton 
                size="small" 
                onClick={() => setQuickSaleModalOpen(false)}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflow: 'hidden', height: 'calc(100% - 48px)' }}>
          {quickSaleModalOpen && screenData?.id && (
            <QuickSaleScreen 
              screenId={screenData.id.toString()}
              isPackageMode={true}
              packageCustomer={{
                id: currentCustomer?.id || 0,
                name: currentCustomer?.name || '',
                phone: currentCustomer?.phone || '',
                addressId: selectedAddressId || 0,
                address: currentCustomer?.addresses?.find(a => a.id === selectedAddressId)?.address || '',
              }}
              packageCashierInfo={currentCashier}
              addOnOrderId={addOnOrderId || undefined}
              onPackageOrderComplete={(orderId, meta) => {
                console.log(meta?.isAdditionalOrder ? '✅ İlave sipariş tamamlandı:' : '✅ Paket siparişi tamamlandı:', orderId, meta);
                setQuickSaleModalOpen(false);
                setAddOnOrderId(null);
                loadPackageOrders();

                // Telefon paket siparişi onayı sonrası mutfak fişini her zaman otomatik bas.
                // Kullanıcı kararı: ayardan bağımsız (autoPrintKitchen kontrolü YAPILMAYACAK).
                // Bu yol SADECE QuickSale üzerinden manuel oluşturulan paket siparişlerinde
                // tetiklenir; platform siparişleri (Getir/YS/Trendyol/Migros) bu callback'i
                // hiç çağırmaz, dolayısıyla onların akışları etkilenmez.
                //
                // ▼ İlave sipariş akışı (meta.isAdditionalOrder === true):
                //   - Backend `addItemsToOrder` çağrısından dönen `addedItemIds`
                //     listesi `itemIdsToPrint` olarak hook'a iletilir.
                //   - Hook telefon bloğunda yalnızca bu ID'lere sahip item'ları
                //     mutfak fişine yazar (delta print).
                //   - `isAdditionalOrder` bayrağı fiş başlığını "⚠️ İLAVE SİPARİŞ ⚠️"
                //     olarak değiştirir, mutfağın yeni eklenenleri anında ayırt
                //     etmesini sağlar.
                if (orderId) {
                  const printOptions = meta?.isAdditionalOrder
                    ? {
                        itemIdsToPrint: meta.addedItemIds,
                        isAdditionalOrder: true,
                      }
                    : undefined;
                  Promise.resolve(handleKitchenPrint(orderId, 'phone', printOptions)).catch((err: unknown) => {
                    console.error('[PaketOnay] Mutfak yazdırma hatası:', err);
                  });

                  // ─── Adisyon (müşteri fişi) oto-yazdırma ────────────────────
                  // Kullanıcı kararı: telefon paket siparişi ilk onayında
                  // mutfak fişiyle birlikte adisyon fişi de OTOMATİK basılır.
                  // Ayardan bağımsız (autoPrintReceiptOnAccept kontrolü
                  // YAPILMAYACAK), tıpkı mutfak fişi gibi.
                  //
                  // İlave sipariş akışında adisyon BASILMAZ — adisyon ilk
                  // siparişle birlikte zaten basıldı; ilave için yalnızca
                  // mutfak delta-print fişi yeterli.
                  if (!meta?.isAdditionalOrder) {
                    Promise.resolve(handleReceiptPrint(orderId, 'phone')).catch((err: unknown) => {
                      console.error('[PaketOnay] Adisyon yazdırma hatası:', err);
                    });
                  }
                }

                // Birleşik kuyruk: sipariş tamamlandı — kuyrukta bekleyen
                // telefon varsa ekran karar modalını açar (sessiz geçiş yok).
                onPackageOrderFlowCompleted?.();
              }}
              onClose={() => {
                setQuickSaleModalOpen(false);
                setAddOnOrderId(null);
              }}
              onSettingsReady={(openSettings) => setQuickSaleOpenSettings(() => openSettings)}
              onProductSortReady={(openProductSort) => setQuickSaleOpenProductSort(() => openProductSort)}
            />
          )}
        </Box>
      </Dialog>

      {/* ================================================================== */}
      {/* ÖDEME AL MODALI */}
      {/* ================================================================== */}
      <PaymentModal
        open={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        selectedOrder={selectedOrder}
        isProcessingPayment={isProcessingPayment}
        cashPaymentEnabled={!!screenData?.paymentSettings?.cashPaymentCashboxId}
        posOptions={(screenData?.paymentSettings?.posOptions || []) as PosOption[]}
        otherOptions={(screenData?.paymentSettings?.otherOptions || []) as OtherPaymentOption[]}
        onCashPayment={handleCashPayment}
        onPosPayment={handlePosPayment as any}
        onOtherPayment={handleOtherPayment}
        onOnlinePayment={handleOnlinePaymentFromModal}
        onPavoP2pPayment={handlePavoP2pPayment}
        hasActivePavoPayment={hasActivePavoPayment}
        onOpenPavoTracking={onOpenPavoTracking}
      />

      {/* ================================================================== */}
      {/* ELLE TELEFON GİRİŞİ MODALI (WhatsApp vs. siparişler için) */}
      {/* ================================================================== */}
      <ManualPhoneModal
        open={isManualPhoneModalOpen}
        onClose={() => setIsManualPhoneModalOpen(false)}
        phoneNumber={manualPhoneNumber}
        setPhoneNumber={setManualPhoneNumber}
        isSearching={isSearchingPhone}
        onSearch={handleManualPhoneSearch}
      />

      {/* ================================================================== */}
      {/* SNACKBAR BİLDİRİMİ */}
      {/* ================================================================== */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ================================================================== */}
      {/* KURYE SEÇME MODAL */}
      {/* ================================================================== */}
      <SelectCourierModal
        open={selectCourierModalOpen}
        onClose={() => setSelectCourierModalOpen(false)}
        onSelectCourier={handleCourierSelect}
        companyId={companyId || ''}
        branchId={screenData?.branchId}
        orderInfo={selectedOrder ? {
          orderId: selectedOrder.id,
          customerName: selectedOrder.customerName || 'Bilinmeyen Müşteri',
          address: selectedOrder.customerAddress || 'Adres bilgisi yok',
        } : undefined}
      />

      {/* ================================================================== */}
      {/* KASİYER GİRİŞ MODAL */}
      {/* ================================================================== */}
      {screenData?.useCashierSystem && (
        <CashierLoginModal
          open={isCashierLoginModalOpen}
          onClose={() => {
            if (currentCashier) {
              setIsCashierLoginModalOpen(false);
            }
          }}
          onLogin={handleCashierLogin}
          screenType="QuickSale"
          quickSaleScreenId={screenData.id}
          allowClose={!!currentCashier}
          title={currentCashier
            ? t('orderManagement.packageOrderModals.changeCashier.title')
            : t('orderManagement.packageOrderModals.cashierLoginRequired.title')}
        />
      )}

      {/* ================================================================== */}
      {/* YENİ SİPARİŞ BİLDİRİM MODAL — aktif kuyruk siparişi (O6)            */}
      {/* ================================================================== */}
      {/* `key={activeNewOrder?.key}` ile React clean remount yapar; sayım    */}
      {/* internal ref'leri yeni sipariş geldiğinde sıfırdan başlar (Seçenek B). */}
      {/* Bu O4 dependency fix'i ile bel-and-suspenders koruması — iki katmanlı.*/}
      <NewOrderModal
        key={activeNewOrder?.key || 'no-active-order'}
        open={!!activeNewOrder}
        onClose={handleCloseNewOrderModal}
        order={activeNewOrder?.masaSiparisi ?? null}
        trendyolOrder={activeNewOrder?.trendyolOrder ?? null}
        autoAcceptSeconds={autoAcceptSeconds}
        onAutoAccept={onAutoAccept}
        onAcceptAndPrint={onAcceptAndPrint}
      />

      {/* NOT: Bekleyen yemek bildirimleri yukarıdaki "BİRLEŞİK PENDING STACK" */}
      {/* render bloğunda telefon pending'leriyle kronolojik FIFO sırayla     */}
      {/* render ediliyor. Burada ayrı bir blok YOK.                          */}

      {/* ================================================================== */}
      {/* GETİR SİPARİŞ DETAY MODALI (DB'den) */}
      {/* ================================================================== */}
      <GetirOrderDetailModal
        open={getirDetailModalOpen}
        onClose={() => {
          setGetirDetailModalOpen(false);
          setGetirDetailOrderId(null);
        }}
        orderId={getirDetailOrderId}
        companyId={companyId || null}
        onRequestOnlineReceipt={onRequestOnlineReceipt && getirDetailOrderId
          ? () => onRequestOnlineReceipt(getirDetailOrderId, 'getir')
          : undefined}
        screenSettings={screenData ? {
          printingSettings: screenData.printingSettings ? {
            defaultReceiptPrinter: screenData.printingSettings.defaultReceiptPrinter || undefined,
            adisyonPrinter: screenData.printingSettings.adisyonPrinter || null,
            receiptDisplaySettings: screenData.printingSettings.receiptDisplaySettings,
          } : undefined,
          localServiceUrl: screenData.localServiceUrl || undefined,
          platformIntegrations: screenData.platformIntegrations,
        } : undefined}
      />

      {/* ================================================================== */}
      {/* GETİR SİPARİŞ İPTAL BİLDİRİM MODALI */}
      {/* ================================================================== */}
      <Dialog
        open={getirCancelModalOpen}
        onClose={handleCloseGetirCancelModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '3px solid #f44336',
            animation: 'pulse 1s ease-in-out infinite',
            '@keyframes pulse': {
              '0%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.4)' },
              '70%': { boxShadow: '0 0 0 15px rgba(244, 67, 54, 0)' },
              '100%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)' },
            },
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#f44336', color: 'white', textAlign: 'center', py: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            ❌ Getir Sipariş İptal Edildi
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 4 }}>
          {cancelledOrderInfo && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {cancelledOrderInfo.confirmationId && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1" color="text.secondary">Doğrulama Kodu:</Typography>
                  <Typography variant="h6" fontWeight="bold">{cancelledOrderInfo.confirmationId}</Typography>
                </Box>
              )}
              {cancelledOrderInfo.clientName && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1" color="text.secondary">Müşteri:</Typography>
                  <Typography variant="body1" fontWeight="bold">{cancelledOrderInfo.clientName}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" color="text.secondary">İptal Nedeni:</Typography>
                <Typography variant="body1" fontWeight="bold" color="error.main">
                  {cancelledOrderInfo.cancelReasonText}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" color="text.secondary">İptal Tipi:</Typography>
                <Chip 
                  label={cancelledOrderInfo.status === 1500 ? 'Admin İptal' : 'Müşteri/Platform İptal'} 
                  color="error" 
                  size="small" 
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" color="text.secondary">İptal Zamanı:</Typography>
                <Typography variant="body1">
                  {new Date(cancelledOrderInfo.cancelledAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCloseGetirCancelModal} 
            variant="contained" 
            color="error" 
            fullWidth
            size="large"
          >
            TAMAM
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================== */}
      {/* QR MENÜ / E-TİCARET / KIOSK SİPARİŞ DETAY MODALI */}
      {/* ================================================================== */}
      <QrOrderDetailModal
        open={qrOrderDetailModalOpen}
        onClose={() => setQrOrderDetailModalOpen(false)}
        order={selectedQrOrder}
        onUpdateStatus={updateMasaSiparisiStatus}
        onRefreshOrders={async () => { fetchMasaSiparisleri(); }}
        companyId={companyId}
        onRequestOnlineReceipt={onRequestOnlineReceipt && selectedQrOrder?.id
          ? () => onRequestOnlineReceipt(selectedQrOrder.id, (selectedQrOrder as any)?.kanal === 'qr' ? 'qr' : 'ecommerce')
          : undefined}
        screenData={screenData ? {
          id: screenData.id,
          localServiceUrl: screenData.localServiceUrl || undefined,
          printingSettings: screenData.printingSettings ? {
            printingMode: screenData.printingSettings.printingMode,
            defaultReceiptPrinter: screenData.printingSettings.defaultReceiptPrinter,
            receiptDisplaySettings: screenData.printingSettings.receiptDisplaySettings,
          } : undefined,
          platformIntegrations: screenData.platformIntegrations,
        } : undefined}
      />

      {/* ================================================================== */}
      {/* GETİR RESTORAN KAPALI MODAL */}
      {/* ================================================================== */}
      <Dialog
        open={getirRestaurantClosedModalOpen}
        onClose={() => setGetirRestaurantClosedModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#d32f2f', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingCartIcon />
          {t('orderManagement.packageOrderModals.getirRestaurantClosed.title')}
            </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2, mt: 1 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {t('orderManagement.packageOrderModals.getirRestaurantClosed.body')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
            {t('orderManagement.packageOrderModals.getirRestaurantClosed.question')}
                    </Typography>
            </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button
                  variant="outlined"
            color="inherit"
            onClick={() => setGetirRestaurantClosedModalOpen(false)}
            disabled={isAnyOpening(getirRestaurantOpening)}
          >
            {t('orderManagement.packageOrderModals.getirRestaurantClosed.cancel')}
                </Button>
          <StoreOpenButtons
            stores={platformStores?.getir || []}
            openingMap={getirRestaurantOpening}
            setOpeningMap={setGetirRestaurantOpening}
            onOpen={openGetirRestaurant}
            confirmLabel={t('orderManagement.packageOrderModals.getirRestaurantClosed.confirm')}
            openingLabel={t('orderManagement.packageOrderModals.states.opening')}
            onSuccess={(storeLabel) => setSnackbar({
              open: true,
              message: storeLabel
                ? `${storeLabel}: ${t('orderManagement.packageOrderModals.snackbar.getirOpened')}`
                : t('orderManagement.packageOrderModals.snackbar.getirOpened'),
              severity: 'success',
            })}
            onFailure={(message) => setSnackbar({
              open: true,
              message: t('orderManagement.packageOrderModals.snackbar.getirOpenFailed', { message: message || t('orderManagement.paymentVerification.common.unknownError') }),
              severity: 'error',
            })}
            onAllOpened={() => setGetirRestaurantClosedModalOpen(false)}
          />
            </DialogActions>
      </Dialog>

      {/* MIGROS SIPARIS IPTAL BILDIRIM MODALI */}
      <Dialog
        open={migrosCancelModalOpen}
        onClose={handleCloseMigrosCancelModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '3px solid #f44336',
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#f44336', color: 'white', textAlign: 'center', py: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            Migros Siparis Iptal Edildi
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 4 }}>
          {migrosCancelledOrderInfo && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {migrosCancelledOrderInfo.shortCode && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1" color="text.secondary">Siparis Kodu:</Typography>
                  <Typography variant="h6" fontWeight="bold">{migrosCancelledOrderInfo.shortCode}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" color="text.secondary">Durum:</Typography>
                <Chip label={migrosCancelledOrderInfo.newStatus || 'Iptal'} color="error" size="small" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" color="text.secondary">Iptal Zamani:</Typography>
                <Typography variant="body1">
                  {new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseMigrosCancelModal} variant="contained" color="error" fullWidth size="large">
            TAMAM
          </Button>
        </DialogActions>
      </Dialog>

      {/* MIGROS RESTORAN KAPALI MODAL */}
      <Dialog
        open={migrosRestaurantClosedModalOpen}
        onClose={() => setMigrosRestaurantClosedModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#d32f2f', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingCartIcon />
          Migros Restoran Kapali
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2, mt: 1 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Migros restoraniniz su anda <strong>kapali</strong> durumda.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Restorani acmak istiyor musunuz? Acildiginda Migros uzerinden yeni siparisler almaya baslayacaksiniz.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setMigrosRestaurantClosedModalOpen(false)}
            disabled={isAnyOpening(migrosRestaurantOpening)}
          >
            Iptal
          </Button>
          <StoreOpenButtons
            stores={platformStores?.migros || []}
            openingMap={migrosRestaurantOpening}
            setOpeningMap={setMigrosRestaurantOpening}
            onOpen={openMigrosRestaurant}
            confirmLabel="Restorani Ac"
            openingLabel="Aciliyor..."
            onSuccess={(storeLabel) => setSnackbar({
              open: true,
              message: storeLabel
                ? `${storeLabel}: Migros restorani basariyla acildi!`
                : 'Migros restorani basariyla acildi!',
              severity: 'success',
            })}
            onFailure={() => setSnackbar({ open: true, message: 'Migros restorani acilamadi. Lutfen tekrar deneyin.', severity: 'error' })}
            onAllOpened={() => setMigrosRestaurantClosedModalOpen(false)}
          />
        </DialogActions>
      </Dialog>

      {/* YEMEKSEPETI VENDOR KAPALI MODAL */}
      <Dialog
        open={ysVendorClosedModalOpen}
        onClose={() => setYsVendorClosedModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#e31e24', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestaurantMenuIcon />
          {t('orderManagement.packageOrderModals.vendorClosed.title', { platform: 'Yemeksepeti' })}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2, mt: 1 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {t('orderManagement.packageOrderModals.vendorClosed.body', { platform: 'Yemeksepeti' })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('orderManagement.packageOrderModals.vendorClosed.question', { platform: 'Yemeksepeti' })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setYsVendorClosedModalOpen(false)}
            disabled={isAnyOpening(ysVendorOpening)}
          >
            {t('orderManagement.packageOrderModals.vendorClosed.cancel')}
          </Button>
          <StoreOpenButtons
            stores={platformStores?.yemeksepeti || []}
            openingMap={ysVendorOpening}
            setOpeningMap={setYsVendorOpening}
            onOpen={openYemeksepetiVendor}
            confirmLabel={t('orderManagement.packageOrderModals.vendorClosed.confirm')}
            openingLabel={t('orderManagement.packageOrderModals.states.opening')}
            onSuccess={(storeLabel) => setSnackbar({
              open: true,
              message: storeLabel
                ? `${storeLabel}: ${t('orderManagement.packageOrderModals.vendorClosed.openSuccess', { platform: 'Yemeksepeti' })}`
                : t('orderManagement.packageOrderModals.vendorClosed.openSuccess', { platform: 'Yemeksepeti' }),
              severity: 'success',
            })}
            onFailure={() => setSnackbar({ open: true, message: t('orderManagement.packageOrderModals.vendorClosed.openFailed', { platform: 'Yemeksepeti' }), severity: 'error' })}
            onAllOpened={() => setYsVendorClosedModalOpen(false)}
          />
        </DialogActions>
      </Dialog>

      {/* TRENDYOL YEMEK RESTORAN KAPALI MODAL */}
      <Dialog
        open={trendyolVendorClosedModalOpen}
        onClose={() => setTrendyolVendorClosedModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#F27A1A', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestaurantMenuIcon />
          {t('orderManagement.packageOrderModals.vendorClosed.title', { platform: 'Trendyol Yemek' })}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2, mt: 1 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {t('orderManagement.packageOrderModals.vendorClosed.body', { platform: 'Trendyol Yemek' })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('orderManagement.packageOrderModals.vendorClosed.question', { platform: 'Trendyol Yemek' })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setTrendyolVendorClosedModalOpen(false)}
            disabled={isAnyOpening(trendyolVendorOpening)}
          >
            {t('orderManagement.packageOrderModals.vendorClosed.cancel')}
          </Button>
          <StoreOpenButtons
            stores={platformStores?.trendyol || []}
            openingMap={trendyolVendorOpening}
            setOpeningMap={setTrendyolVendorOpening}
            onOpen={openTrendyolVendor}
            confirmLabel={t('orderManagement.packageOrderModals.vendorClosed.confirm')}
            openingLabel={t('orderManagement.packageOrderModals.states.opening')}
            onSuccess={(storeLabel) => setSnackbar({
              open: true,
              message: storeLabel
                ? `${storeLabel}: ${t('orderManagement.packageOrderModals.vendorClosed.openSuccess', { platform: 'Trendyol Yemek' })}`
                : t('orderManagement.packageOrderModals.vendorClosed.openSuccess', { platform: 'Trendyol Yemek' }),
              severity: 'success',
            })}
            onFailure={() => setSnackbar({ open: true, message: t('orderManagement.packageOrderModals.vendorClosed.openFailed', { platform: 'Trendyol Yemek' }), severity: 'error' })}
            onAllOpened={() => setTrendyolVendorClosedModalOpen(false)}
          />
        </DialogActions>
      </Dialog>

      {/* YEMEKSEPETI SIPARIS REDDETME MODALI */}
      <Dialog
        open={ysRejectModalOpen}
        onClose={() => { setYsRejectModalOpen(false); setYsRejectOrderId(null); setSelectedYsRejectReason(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#e31e24', color: 'white' }}>
          Yemeksepeti Siparis Reddet / Iptal Et
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 4, mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Lutfen red sebebini secin.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {ysRejectReasons.map((reason: any) => {
              const reasonKey = reason.reasonId || reason.ReasonId || reason.code || reason.reason;
              const reasonDesc = reason.description || reason.Description || reason.reason || reasonKey;
              return (
                <Button
                  key={reasonKey}
                  variant={selectedYsRejectReason === reasonKey ? 'contained' : 'outlined'}
                  color={selectedYsRejectReason === reasonKey ? 'error' : 'inherit'}
                  onClick={() => setSelectedYsRejectReason(reasonKey)}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                >
                  {reasonDesc}
                </Button>
              );
            })}
            {ysRejectReasons.length === 0 && (
              <Typography variant="body2" color="text.secondary" align="center">
                Red sebepleri yukleniyor...
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => { setYsRejectModalOpen(false); setYsRejectOrderId(null); setSelectedYsRejectReason(null); }}
          >
            Iptal
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!selectedYsRejectReason}
            onClick={async () => {
              if (!ysRejectOrderId || !selectedYsRejectReason) return;
              try {
                const result = await updateYemeksepetiOrderStatus(ysRejectOrderId, 'reject', selectedYsRejectReason);
                if (result.success) {
                  setSnackbar({ open: true, message: 'Yemeksepeti siparis reddedildi', severity: 'info' });
                  setYsRejectModalOpen(false);
                  setYsRejectOrderId(null);
                  setSelectedYsRejectReason(null);
                  await fetchYemeksepetiOrders();
                } else {
                  setSnackbar({ open: true, message: result.message || 'Siparis reddedilemedi', severity: 'error' });
                }
              } catch (err) {
                setSnackbar({ open: true, message: 'Bir hata olustu', severity: 'error' });
              }
            }}
          >
            Reddet
          </Button>
        </DialogActions>
      </Dialog>

      {/* YEMEKSEPETI SIPARIS IPTAL BILDIRIM MODALI */}
      {/*
        Eskiden modal yalnızca "Durum" ve `customerName` gösteriyordu — bunlar
        bile çoğu zaman boş geliyordu çünkü:
          - SignalR cancel handler'ı sadece 4 alanlık dar bir özet üretiyor
          - Polling fallback'i ise sadece orderId+status pas ediyordu
        Artık `ysCancelledOrderInfo.rawOrder` üzerinden tüm YS sipariş objesi
        modal'a geliyor (`SignalR.YemeksepetiOrderCancelled.orderData` veya
        polling'te `yemeksepetiOrders` listesinden bulunan order). Bu sayede
        sipariş no / müşteri / telefon / adres / toplam / iptal saati gibi
        bilgileri kullanıcıya gösterebiliyoruz.
      */}
      <Dialog
        open={ysCancelModalOpen}
        onClose={() => { setYsCancelModalOpen(false); setYsCancelledOrderInfo(null); fetchYemeksepetiOrders(); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#e31e24', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">Yemeksepeti Sipariş İptal Edildi</Typography>
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 4 }}>
          {ysCancelledOrderInfo && (() => {
            const info: any = ysCancelledOrderInfo;
            const raw: any = info.rawOrder || {};
            // Alan adları PascalCase + camelCase fallback'lı; backend her iki
            // şekilde de döndürebiliyor (DTO vs. raw entity).
            const orderNumber = raw.shortCode || raw.ShortCode || raw.code || raw.Code
              || raw.displayOrderId || raw.DisplayOrderId
              || (info.orderId ? `#${info.orderId}` : null);
            const customerName = info.customerName
              || raw.customerFullName || raw.CustomerFullName
              || raw.customerName || raw.CustomerName;
            const customerPhone = raw.customerPhone || raw.CustomerPhone
              || raw.customerPhoneNumber || raw.CustomerPhoneNumber;
            const address = [
              raw.deliveryAddress ?? raw.DeliveryAddress,
              raw.deliveryDistrict ?? raw.DeliveryDistrict,
              raw.deliveryCity ?? raw.DeliveryCity,
            ].filter(Boolean).join(', ');
            const totalPrice = raw.totalPrice ?? raw.TotalPrice ?? raw.grandTotal ?? raw.GrandTotal;
            const createdAtRaw = raw.createdAt || raw.CreatedAt
              || raw.approvedDate || raw.ApprovedDate;
            const formatDate = (v?: string) => {
              if (!v) return null;
              try {
                const d = v.includes('Z') || v.includes('+') ? new Date(v) : new Date(v + 'Z');
                return new Intl.DateTimeFormat('tr-TR', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                  timeZone: 'Europe/Istanbul',
                }).format(d);
              } catch { return null; }
            };
            const orderTimeText = formatDate(createdAtRaw);
            const cancelTimeText = formatDate(info.cancelledAt);
            // YS API iptal sebebini birkaç farklı alanda gönderebiliyor.
            const cancelReason = raw.cancellationReason || raw.CancellationReason
              || raw.cancelReason || raw.CancelReason
              || raw.rejectionReason || raw.RejectionReason;
            // rawJsonData içinde müşteri yorumu olabilir — parse hata vermesin.
            let customerComment: string | undefined;
            try {
              const rj = raw.rawJsonData || raw.RawJsonData;
              if (rj) {
                const parsed = typeof rj === 'string' ? JSON.parse(rj) : rj;
                customerComment = parsed?.comments?.customerComment;
              }
            } catch { /* yoksay */ }

            const rowSx = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 };
            const labelSx = { color: 'text.secondary', flexShrink: 0 } as const;
            const valueSx = { fontWeight: 'bold', textAlign: 'right' as const, wordBreak: 'break-word' as const };

            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                <Box sx={rowSx}>
                  <Typography variant="body1" sx={labelSx}>Durum:</Typography>
                  <Chip label={info.status || 'İptal'} color="error" size="small" />
                </Box>
                {orderNumber && (
                  <Box sx={rowSx}>
                    <Typography variant="body1" sx={labelSx}>Sipariş No:</Typography>
                    <Typography variant="body1" sx={valueSx}>{orderNumber}</Typography>
                  </Box>
                )}
                {customerName && (
                  <Box sx={rowSx}>
                    <Typography variant="body1" sx={labelSx}>Müşteri:</Typography>
                    <Typography variant="body1" sx={valueSx}>{customerName}</Typography>
                  </Box>
                )}
                {customerPhone && (
                  <Box sx={rowSx}>
                    <Typography variant="body1" sx={labelSx}>Telefon:</Typography>
                    <Typography variant="body1" sx={valueSx}>{customerPhone}</Typography>
                  </Box>
                )}
                {address && (
                  <Box sx={rowSx}>
                    <Typography variant="body1" sx={labelSx}>Adres:</Typography>
                    <Typography variant="body2" sx={valueSx}>{address}</Typography>
                  </Box>
                )}
                {typeof totalPrice === 'number' && totalPrice > 0 && (
                  <Box sx={rowSx}>
                    <Typography variant="body1" sx={labelSx}>Toplam:</Typography>
                    <Typography variant="body1" sx={{ ...valueSx, color: 'error.main' }}>
                      {totalPrice.toFixed(2)} TL
                    </Typography>
                  </Box>
                )}
                {orderTimeText && (
                  <Box sx={rowSx}>
                    <Typography variant="body1" sx={labelSx}>Sipariş Saati:</Typography>
                    <Typography variant="body2" sx={valueSx}>{orderTimeText}</Typography>
                  </Box>
                )}
                {cancelTimeText && (
                  <Box sx={rowSx}>
                    <Typography variant="body1" sx={labelSx}>İptal Saati:</Typography>
                    <Typography variant="body2" sx={valueSx}>{cancelTimeText}</Typography>
                  </Box>
                )}
                {cancelReason && (
                  <Box sx={rowSx}>
                    <Typography variant="body1" sx={labelSx}>İptal Sebebi:</Typography>
                    <Typography variant="body2" sx={valueSx}>{cancelReason}</Typography>
                  </Box>
                )}
                {customerComment && (
                  <Box sx={rowSx}>
                    <Typography variant="body1" sx={labelSx}>Müşteri Notu:</Typography>
                    <Typography variant="body2" sx={valueSx}>{customerComment}</Typography>
                  </Box>
                )}
                {!orderNumber && !customerName && !customerPhone && !address && !totalPrice && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
                    Bu sipariş için detaylı bilgi alınamadı. Sipariş listesinde son durumu görebilirsiniz.
                  </Typography>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => { setYsCancelModalOpen(false); setYsCancelledOrderInfo(null); fetchYemeksepetiOrders(); }}
          >
            Tamam
          </Button>
        </DialogActions>
      </Dialog>

      {/* MIGROS SIPARIS REDDETME/IPTAL MODALI */}
      <Dialog
        open={migrosRejectModalOpen}
        onClose={() => { setMigrosRejectModalOpen(false); setMigrosRejectOrderId(null); setMigrosRejectOrderStatus(''); setSelectedMigrosCancelReasonId(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: migrosRejectOrderStatus === 'NEW_PENDING' ? '#ff9800' : '#f44336', color: 'white' }}>
          {migrosRejectOrderStatus === 'NEW_PENDING' ? 'Migros Siparis Reddet' : 'Migros Siparis Iptal Et'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {migrosRejectOrderStatus === 'NEW_PENDING'
              ? 'Onay bekleyen siparisi reddetmek icin bir sebep secin.'
              : 'Onaylanmis siparisi iptal etmek icin bir sebep secin.'}
          </Typography>
          {migrosCancelReasons.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Iptal sebepleri yukleniyor...</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {migrosCancelReasons.map((reason: any) => (
                <Button
                  key={reason.reasonId}
                  variant={selectedMigrosCancelReasonId === reason.reasonId ? 'contained' : 'outlined'}
                  color={selectedMigrosCancelReasonId === reason.reasonId ? 'warning' : 'inherit'}
                  onClick={() => setSelectedMigrosCancelReasonId(reason.reasonId)}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                >
                  {reason.description || `Sebep #${reason.reasonId}`}
                </Button>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => { setMigrosRejectModalOpen(false); setMigrosRejectOrderId(null); setMigrosRejectOrderStatus(''); setSelectedMigrosCancelReasonId(null); }}
          >
            Vazgec
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!selectedMigrosCancelReasonId}
            onClick={async () => {
              if (!migrosRejectOrderId || !selectedMigrosCancelReasonId) return;
              try {
                const isReject = migrosRejectOrderStatus === 'NEW_PENDING';
                const endpoint = isReject
                  ? `/api/migros-orders/reject`
                  : `/api/migros-orders/cancel`;

                const body = { OrderId: migrosRejectOrderId, CancelReasonId: selectedMigrosCancelReasonId };

                const resp = await migrosApi.post(endpoint, body);
                const result = resp.data;
                if (result.success) {
                  setSnackbar({ open: true, message: isReject ? 'Migros siparis reddedildi' : 'Migros siparis iptal edildi', severity: 'success' });
                  await fetchMigrosOrders();
                } else {
                  setSnackbar({ open: true, message: result.message || (isReject ? 'Reddetme basarisiz' : 'Iptal basarisiz'), severity: 'error' });
                }
              } catch (err) {
                setSnackbar({ open: true, message: 'Islem hatasi', severity: 'error' });
              } finally {
                setMigrosRejectModalOpen(false);
                setMigrosRejectOrderId(null);
                setMigrosRejectOrderStatus('');
                setSelectedMigrosCancelReasonId(null);
              }
            }}
          >
            {migrosRejectOrderStatus === 'NEW_PENDING' ? 'Reddet' : 'Iptal Et'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MIGROS SIPARIS DETAY MODALI */}
      {migrosDetailModalOpen && migrosDetailOrderId && (
        <MigrosOrderDetailModal
          open={migrosDetailModalOpen}
          onClose={() => { setMigrosDetailModalOpen(false); setMigrosDetailOrderId(null); }}
          orderId={migrosDetailOrderId}
          companyId={companyId || ''}
          onRequestOnlineReceipt={onRequestOnlineReceipt
            ? () => onRequestOnlineReceipt(migrosDetailOrderId, 'migros')
            : undefined}
          screenData={screenData ? {
            id: screenData.id,
            localServiceUrl: screenData.localServiceUrl || undefined,
            printingSettings: screenData.printingSettings ? {
              printingMode: screenData.printingSettings.printingMode,
              defaultReceiptPrinter: screenData.printingSettings.defaultReceiptPrinter,
              adisyonPrinter: screenData.printingSettings.adisyonPrinter || null,
              receiptDisplaySettings: screenData.printingSettings.receiptDisplaySettings,
            } : undefined,
            platformIntegrations: screenData.platformIntegrations,
          } : undefined}
        />
      )}

      {/* YEMEKSEPETI SIPARIS DETAY MODALI */}
      {ysDetailModalOpen && ysDetailOrderId && (
        <YemeksepetiOrderDetailModal
          open={ysDetailModalOpen}
          onClose={() => { setYsDetailModalOpen(false); setYsDetailOrderId(null); }}
          orderId={ysDetailOrderId}
          companyId={companyId || ''}
          onRequestOnlineReceipt={onRequestOnlineReceipt
            ? () => onRequestOnlineReceipt(ysDetailOrderId, 'yemeksepeti')
            : undefined}
          screenData={screenData ? {
            id: screenData.id,
            localServiceUrl: screenData.localServiceUrl || undefined,
            printingSettings: screenData.printingSettings ? {
              printingMode: screenData.printingSettings.printingMode,
              defaultReceiptPrinter: screenData.printingSettings.defaultReceiptPrinter,
              adisyonPrinter: screenData.printingSettings.adisyonPrinter || null,
              receiptDisplaySettings: screenData.printingSettings.receiptDisplaySettings,
            } : undefined,
            platformIntegrations: screenData.platformIntegrations,
          } : undefined}
        />
      )}

      {/* TRENDYOL YEMEK SIPARIS DETAY MODALI */}
      <TrendyolOrderDetailModal
        open={trendyolDetailModalOpen}
        onClose={() => { setTrendyolDetailModalOpen(false); setTrendyolDetailOrder(null); }}
        order={trendyolDetailOrder}
        trendyolProductDescriptions={trendyolProductDescriptions}
        onRequestOnlineReceipt={(() => {
          if (!onRequestOnlineReceipt || !trendyolDetailOrder) return undefined;
          // Trendyol detay modalindeki ham `order` objesinin `id` alanı, Trendyol
          // API'sinin internal hash'idir (örn. SHA-256). PackageOrder.id ise
          // helpers.convertTrendyolToPackageOrder içinde
          // parseInt(orderId) || hashCode(id) ile üretilir. PackageOrderScreen'in
          // `onRequestOnlineReceipt` matcher'ı bu numeric id ile arar; bu yüzden
          // burada ham hash yerine aynı normalize fonksiyonunu kullanmalıyız.
          const normalizedId = getTrendyolPackageOrderId(trendyolDetailOrder as { orderId?: string | number; id?: string | number });
          if (normalizedId === null) return undefined;
          return () => onRequestOnlineReceipt(normalizedId, 'trendyol');
        })()}
        screenData={screenData ? {
          id: screenData.id,
          localServiceUrl: screenData.localServiceUrl || undefined,
          printingSettings: screenData.printingSettings ? {
            printingMode: screenData.printingSettings.printingMode,
            defaultReceiptPrinter: screenData.printingSettings.defaultReceiptPrinter,
            adisyonPrinter: screenData.printingSettings.adisyonPrinter || null,
            receiptDisplaySettings: screenData.printingSettings.receiptDisplaySettings,
          } : undefined,
          platformIntegrations: screenData.platformIntegrations,
        } : undefined}
      />

      {/* TRENDYOL YEMEK IPTAL MODALI */}
      <TrendyolCancelModal
        open={trendyolCancelModalOpen}
        onClose={() => { setTrendyolCancelModalOpen(false); setTrendyolCancelOrder(null); }}
        order={trendyolCancelOrder}
        onConfirm={async (packageId, itemIdList, reasonId) => {
          const success = await cancelTrendyolOrder(packageId, itemIdList, reasonId);
          if (success) {
            setSnackbar({ open: true, message: 'Trendyol sipariş iptal edildi', severity: 'success' });
          }
          return success;
        }}
      />

      {/* TRENDYOL SIPARIS IPTAL BILDIRIM MODALI */}
      <Dialog
        open={trendyolCancelNotifModalOpen}
        onClose={handleCloseTrendyolCancelNotifModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '3px solid #f44336',
            animation: 'pulse 1s ease-in-out infinite',
            '@keyframes pulse': {
              '0%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.4)' },
              '70%': { boxShadow: '0 0 0 15px rgba(244, 67, 54, 0)' },
              '100%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)' },
            },
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#f44336', color: 'white', textAlign: 'center', py: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            ❌ Trendyol Sipariş İptal Edildi
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 4 }}>
          {trendyolCancelledNotifOrder && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" color="text.secondary">Sipariş No:</Typography>
                <Typography variant="h6" fontWeight="bold">{trendyolCancelledNotifOrder.orderNumber || trendyolCancelledNotifOrder.orderCode}</Typography>
              </Box>
              {trendyolCancelledNotifOrder.customer && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1" color="text.secondary">Müşteri:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {[trendyolCancelledNotifOrder.customer.firstName, trendyolCancelledNotifOrder.customer.lastName].filter(Boolean).join(' ')}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" color="text.secondary">Durum:</Typography>
                <Chip label={trendyolCancelledNotifOrder.packageStatus || 'Cancelled'} color="error" size="small" />
              </Box>
              {trendyolCancelledNotifOrder.totalPrice != null && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1" color="text.secondary">Tutar:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {Number(trendyolCancelledNotifOrder.totalPrice).toFixed(2)} ₺
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseTrendyolCancelNotifModal}
            variant="contained"
            color="error"
            fullWidth
            size="large"
          >
            TAMAM
          </Button>
        </DialogActions>
      </Dialog>

      {/* RESTORAN KAPATMA ONAY MODALI */}
      <Dialog
        open={closeRestaurantModalOpen}
        onClose={() => { if (!closingRestaurant) { setCloseRestaurantModalOpen(false); setYsCloseReason('OTHER'); } }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{
          bgcolor: closeRestaurantPlatform === 'trendyol' ? '#F27A1A'
            : closeRestaurantPlatform === 'yemeksepeti' ? '#e31e24'
            : closeRestaurantPlatform === 'migros' ? '#F58220'
            : '#00b300',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <RestaurantMenuIcon />
          {closeRestaurantPlatform === 'getir' && `Getir ${t('orderManagement.packageOrderModals.closeRestaurant.title')}`}
          {closeRestaurantPlatform === 'migros' && `Migros ${t('orderManagement.packageOrderModals.closeRestaurant.title')}`}
          {closeRestaurantPlatform === 'yemeksepeti' && `Yemeksepeti ${t('orderManagement.packageOrderModals.closeRestaurant.title')}`}
          {closeRestaurantPlatform === 'trendyol' && `Trendyol ${t('orderManagement.packageOrderModals.closeRestaurant.title')}`}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2, mt: 1 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {closeRestaurantPlatform === 'getir' && 'Getir'}
            {closeRestaurantPlatform === 'migros' && 'Migros'}
            {closeRestaurantPlatform === 'yemeksepeti' && 'Yemeksepeti'}
            {closeRestaurantPlatform === 'trendyol' && 'Trendyol Yemek'}
            {' '}{t('orderManagement.packageOrderModals.closeRestaurant.confirmQuestion')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('orderManagement.packageOrderModals.closeRestaurant.description')}
          </Typography>

          {closableStores.length > 1 && (
            <FormControl fullWidth size="small" sx={{ mt: 1, mb: 1 }}>
              <InputLabel>{t('orderManagement.ordersTable.headers.store')}</InputLabel>
              <Select
                value={closeStoreId}
                label={t('orderManagement.ordersTable.headers.store')}
                onChange={(e) => setCloseStoreId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                {closableStores.map(store => (
                  <MenuItem key={store.integrationId} value={store.integrationId}>{store.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {closeRestaurantPlatform === 'yemeksepeti' && (
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel>{t('orderManagement.packageOrderModals.closeRestaurant.reasonLabel')}</InputLabel>
              <Select
                value={ysCloseReason}
                label={t('orderManagement.packageOrderModals.closeRestaurant.reasonLabel')}
                onChange={(e) => setYsCloseReason(e.target.value)}
              >
                <MenuItem value="TOO_BUSY_KITCHEN">{t('orderManagement.packageOrderModals.closeRestaurant.reasons.kitchenBusy')}</MenuItem>
                <MenuItem value="TOO_BUSY_NO_DRIVERS">{t('orderManagement.packageOrderModals.closeRestaurant.reasons.deliveryBusy')}</MenuItem>
                <MenuItem value="TECHNICAL_PROBLEM">{t('orderManagement.packageOrderModals.closeRestaurant.reasons.technicalIssue')}</MenuItem>
                <MenuItem value="UPDATES_IN_MENU">{t('orderManagement.packageOrderModals.closeRestaurant.reasons.menuUpdate')}</MenuItem>
                <MenuItem value="HOLIDAY_SPECIAL_DAY">{t('orderManagement.packageOrderModals.closeRestaurant.reasons.holidaySpecialDay')}</MenuItem>
                <MenuItem value="REFURBISHMENT">{t('orderManagement.packageOrderModals.closeRestaurant.reasons.refurbishment')}</MenuItem>
                <MenuItem value="BAD_WEATHER">{t('orderManagement.packageOrderModals.closeRestaurant.reasons.weatherCondition')}</MenuItem>
                <MenuItem value="OTHER">{t('orderManagement.packageOrderModals.closeRestaurant.reasons.other')}</MenuItem>
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => { setCloseRestaurantModalOpen(false); setYsCloseReason('OTHER'); }}
            disabled={closingRestaurant}
          >
            {t('orderManagement.packageOrderModals.closeRestaurant.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={closingRestaurant || (closableStores.length > 1 && closeStoreId === '')}
            startIcon={closingRestaurant ? <CircularProgress size={18} color="inherit" /> : null}
            onClick={async () => {
              setClosingRestaurant(true);
              try {
                const reason = closeRestaurantPlatform === 'yemeksepeti' ? ysCloseReason : undefined;
                await handleCloseRestaurantConfirm(reason, closeStoreId === '' ? undefined : closeStoreId);
              } finally {
                setClosingRestaurant(false);
                setYsCloseReason('OTHER');
              }
            }}
          >
            {closingRestaurant
              ? t('orderManagement.packageOrderModals.closeRestaurant.closing')
              : t('orderManagement.packageOrderModals.closeRestaurant.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PackageOrderModals;
